import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';

interface ToolTrayPresence {
    /** True while at least one tool tray is mounted inside the scope. */
    trayOpen: boolean;
    /** Counts one tray as mounted; the returned function counts it out. */
    register: () => () => void;
}

const OUTSIDE_ANY_SCOPE: ToolTrayPresence = {
    trayOpen: false,
    register: () => () => {},
};

const ToolTrayPresenceContext =
    createContext<ToolTrayPresence>(OUTSIDE_ANY_SCOPE);

/**
 * Scope that knows whether any tool tray is mounted inside it. Trays count
 * themselves in on mount and out on unmount, so the scope stays open while
 * any one of several trays is up. Outside a scope no tray is ever reported.
 */
export default function ToolTrayPresenceProvider({
    children,
}: {
    children: ReactNode;
}) {
    const [mountedTrays, setMountedTrays] = useState(0);

    const register = useCallback(() => {
        setMountedTrays((count) => count + 1);
        return () => setMountedTrays((count) => count - 1);
    }, []);

    const presence = useMemo(
        () => ({ trayOpen: mountedTrays > 0, register }),
        [mountedTrays, register]
    );

    return (
        <ToolTrayPresenceContext.Provider value={presence}>
            {children}
        </ToolTrayPresenceContext.Provider>
    );
}

/** Whether a tool tray is mounted in the enclosing scope. */
export function useToolTrayOpen(): boolean {
    return useContext(ToolTrayPresenceContext).trayOpen;
}

/**
 * Counts the calling component as a mounted tool tray of the enclosing scope
 * for as long as it is mounted. Counted before paint, so whatever the scope
 * hides while a tray is up never shows over the tray's first frame.
 */
export function useCountAsOpenToolTray(): void {
    const { register } = useContext(ToolTrayPresenceContext);
    useLayoutEffect(() => register(), [register]);
}
